import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { projectId } from '@/sanity/env';
import { useCallback } from 'react';
import { PortableTextBlock } from 'sanity';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function sanityImageUrl(imageString: string): string {
  if (!imageString.startsWith('image-')) {
    throw new Error('Invalid image string');
  }

  // Remove the 'image-' prefix
  const strippedString = imageString.substring(6);

  // Split the remaining string by '-'
  const parts = strippedString.split('-');

  if (parts.length < 3) {
    throw new Error('Invalid image string format');
  }

  const id = parts[0];
  const dimensions = parts[1];
  const format = parts.slice(2).join('-'); // Handle formats with dashes

  // Construct the filename
  const filename = `${id}-${dimensions}.${format}`;

  // Build the URL
  return `https://cdn.sanity.io/images/${projectId}/production/${filename}`;
}

export class EventEmitter {
  #callbacks = {};

  static Event = class {
    type = '';
    target = '';
    data = '';
  };

  addEventListener(eventType: string, callback: any) {
    if (typeof callback !== 'function') return;
    // @ts-ignore
    if (this.#callbacks[eventType] === undefined) {
      // @ts-ignore
      this.#callbacks[eventType] = [];
    }

    // @ts-ignore
    this.#callbacks[eventType].push(callback);
  }

  // @ts-ignore
  dispatchEvent(eventType, data) {
    // @ts-ignore
    if (this.#callbacks[eventType] === undefined) return;

    const event = new EventEmitter.Event();
    event.type = eventType;
    // @ts-ignore
    event.target = this;
    event.data = data;

    // @ts-ignore
    this.#callbacks[eventType].forEach((callback) => {
      callback(event);
    });
  }
}

export function formatValue(num: number, fractionalDigits: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: fractionalDigits,
    maximumFractionDigits: fractionalDigits,
  }).format(num);
}

export const getTitle = ({ text }: { text: PortableTextBlock[] }) => {
  const titleBlock = text?.find(
    (block: any) =>
      block._type === 'block' &&
      ['h1', 'h2', 'h3'].includes(block.style || 'normal')
  );
  const children = titleBlock?.children as any[];
  return children?.[0]?.text || 'Untitled';
};

export const getDescription = ({ text, charAmount = 250 }: { text: PortableTextBlock[], charAmount?: number }) => {
  const descBlock = text?.find(
    (block: any) => block._type === 'block' && block.style === 'normal'
  );
  const children = descBlock?.children as any[];
  const desc = children?.[0]?.text || '';
  return desc.length > charAmount ? desc.substring(0, charAmount) + '...' : desc;
};
